package org.teddapp.project.sync

import com.tedd.db.TeddDatabase
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.teddapp.project.core.CoreSession
import org.teddapp.project.db.nowMillis
import org.teddapp.project.server.ServerSession
import org.teddapp.project.server.ServerStore
import org.teddapp.project.ui.AppSettings
import org.teddapp.project.ui.settings.SyncBindings

/** Sous quelle clé on retient vers quel serveur et quel compte cet appareil s'est déjà synchronisé. */
private const val KEY_SYNC_ORIGIN = "server.sync_origin"

/**
 * À appeler avant la première synchronisation vers un serveur : si l'appareil change de
 * serveur ou de compte, tout ce qu'il possède doit être renvoyé, et son curseur repart
 * de zéro. Sans cela, les lignes déjà « synchronisées » ailleurs ne partiraient jamais.
 *
 * Retourne vrai si une remise à zéro a eu lieu.
 */
suspend fun prepareServerSync(db: TeddDatabase, session: ServerSession, store: ServerStore): Boolean {
    val origin = "${session.url}|${session.userId}"
    if (store.get(KEY_SYNC_ORIGIN) == origin) return false
    db.transaction {
        db.syncResetQueries.markAllDirty()
        db.syncCursorQueries.clearAll()
    }
    store.set(KEY_SYNC_ORIGIN, origin)
    return true
}

/**
 * Branche la synchronisation sur le serveur teddApp : le même moteur que jusqu'ici, avec
 * le serveur comme distant. Retourne null tant que personne n'est connecté.
 */
fun createServerSyncBindings(
    db: TeddDatabase,
    appSettings: AppSettings,
    scope: CoroutineScope,
    session: ServerSession,
    store: ServerStore,
    http: io.ktor.client.HttpClient,
    coreSession: CoreSession? = null,
): SyncBindings? {
    if (!session.connected || session.userId.isBlank()) return null
    val files = FileTransfer(db, session)
    TeddFiles.transfer = files
    val engine = FileAwareSyncEngine(SqlDelightSyncEngine(db, RustRemoteStore(session, db), Clock { nowMillis() }), files)
    // Le journal du noyau voyage à part : c'est lui qui porte les heures et les paliers.
    // Branché avant le gestionnaire, dont le premier cycle part dès sa création.
    coreSession?.transport = ServerJournalTransport(session, store, origin = "${session.url}|${session.userId}")
    // Sans noyau sur place (web, iOS), c'est le serveur qui rejoue le journal.
    if (coreSession != null && !coreSession.local) {
        coreSession.remote = ServerRemoteCore(session)
        coreSession.sync()
    }
    val manager = SyncManager(db, engine, appSettings, scope, journalCycle = { coreSession?.syncNow() })
    // La remise à zéro éventuelle précède tout cycle : elle décide de ce qui part.
    scope.launch { runCatching { prepareServerSync(db, session, store) } }
    // La sonnette : prévenu par le serveur, l'appareil tire sans attendre.
    val bell = ServerBell(http, session, scope) { manager.remoteChanged() }
    scope.launch { bell.live.collect { manager.bellLive = it } }
    bell.start()
    return object : SyncBindings {
        override val state = manager.state
        override val projectUrl: String = session.url.removePrefix("https://").removePrefix("http://").substringBefore("/")
        override fun onToggle(on: Boolean) { manager.setEnabled(on) }
        override fun onSyncNow() { manager.syncNow() }
        override fun onCheckRemote() { manager.checkRemote() }
    }
}
